import ControllerBase from 'global/js/ControllerBase';
import ObservableState from 'global/js/ObservableState';
import { fromEvent } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { SearchSuggestionResultsEvents } from '../m-search-autosuggestion-hits/m-search-autosuggestion-hits.controller';
import { MoleculeSearchAutosuggestionHitsProps } from '../m-search-autosuggestion-hits/m-search-autosuggestion-hits.data';
import { MoleculeSearchFieldEvent, MoleculeSearchFieldEvents } from '../m-search-field/m-search-field.events';

const PREFIX = 'SearchSuggestionResults__'

export const InputSearchDebounceEvents = {
	RESET_SEARCH: PREFIX + 'RESET_SEARCH',
	CLOSE_FLYOUT: PREFIX + 'CLOSE_FLYOUT'
}

interface ComponentRefs {
	searchInputDebouncedContainer: 'searchInputDebouncedContainer';
	goToResultsPageLink: 'goToResultsPageLink';
}

type InputSearchDebounceState = {
	cache?: Record<string, MoleculeSearchAutosuggestionHitsProps>
	results: MoleculeSearchAutosuggestionHitsProps
}

const defaultState = {
	cache: undefined,
	results: {
		hasResults: false,
		resultsByCategory: [],
		summaryMessage: {
			url: '',
			label: ''
		}
	}
}

export default class ControllerSearchInputDebounce extends ControllerBase<ComponentRefs> {
	private searchInputDebounced: HTMLInputElement;
	private searchInputDebouncedButton: HTMLButtonElement;
	private searchInputDebouncedReset: HTMLSpanElement;
	private searchInputDebouncedContainer: HTMLDivElement;
	private goToResultsPageLink: HTMLAnchorElement;
	private typeaheadUrl: string;
	private minCharLength: number = 2;
	private debounceTime: number = 200;
	private searchInput;
	private searchResultsPage: string | null;

	private state: ObservableState<InputSearchDebounceState>
	private static readonly componentRefs: ComponentRefs = {
		searchInputDebouncedContainer: 'searchInputDebouncedContainer',
		goToResultsPageLink: 'goToResultsPageLink'
	}

	constructor(element: HTMLElement) {
		super(element, [], Object.values(ControllerSearchInputDebounce.componentRefs));

		this.state = new ObservableState<InputSearchDebounceState>(defaultState, {
			ON_UPDATE_RESULTS: {
				callback: this.onUpdateResults,
				deps: ['results']
			},
		});

		this.init();
	}

	private init(): void {
		this.typeaheadUrl = this.$element.dataset.typeaheadurl ?? '';
		this.searchInputDebouncedContainer = this.$references.searchInputDebouncedContainer as HTMLDivElement;
		this.searchInputDebounced = this.searchInputDebouncedContainer.querySelector('input.m-search-field__input') as HTMLInputElement;
		this.searchInputDebouncedButton = this.searchInputDebouncedContainer.querySelector('button.m-search-field__button') as HTMLButtonElement;
		this.searchInputDebouncedReset = this.searchInputDebouncedContainer.querySelector('span.js-reset-input') as HTMLSpanElement;
		this.goToResultsPageLink = this.$references.goToResultsPageLink as HTMLAnchorElement;
		this.searchInput =document.querySelector('.m-search-field__input') as HTMLInputElement;

		this.handleMultipleEvents()
		this.observeUserSearch()
		this.registerEvents()
	}

	private goToResultsPage(): void {
		if (this.searchResultsPage) {
			const searchValue = this.searchInput?.value;
			let search = this.searchResultsPage;
			const qPos = search.indexOf('?q=') + 3;
			const end = search.indexOf('&category');
			if(end){
				search = search.substr(qPos, end - qPos);
				if(search)
					this.searchResultsPage = this.searchResultsPage.replace(search, searchValue);
			}
			window.location.href = this.searchResultsPage;
		}
	}

	/**
	 * Creates events for the differents triggers
	 * @returns void
	 */
	private handleMultipleEvents(): void {
		this.searchInputDebounced
			&& this.searchInputDebounced.addEventListener('keypress', (event: KeyboardEvent) => {
				if (event.key === 'Enter') {
					const input = event.currentTarget as HTMLInputElement
					this.fetchResults(input.value)
					this.goToResultsPage();
				}
			});

		this.searchInputDebouncedButton && this.searchInputDebouncedButton.addEventListener('click', (event: MouseEvent | PointerEvent) => {
			event.preventDefault();
			const value = this.searchInputDebounced.value
			this.fetchResults(value);
			this.goToResultsPage();
		})

		this.searchInputDebouncedReset
			&& this.searchInputDebouncedReset.addEventListener('click', (event: MouseEvent | PointerEvent) => {
				this.resetSearchResults()
			})
	}

	/**
	 * Do an API call for typeahead results
	 * @param  {string} query
	 * @returns Promise
	 */
	private queryTypeaheadResults(query: string): Promise<Response> {
		const url = new URL(this.typeaheadUrl);
		url.searchParams.set('q', query);
		return fetch(url.toString());
	}

	/**
	 * Watch for search queries and trig the fetch
	 * @returns void
	 */
	private observeUserSearch(): void {
		if (this.searchInputDebounced) {
			/** Event handler for when the input changes value */
			const searchKeyupEvent$ = fromEvent(this.searchInputDebounced, 'keyup');
			searchKeyupEvent$
				.pipe(
					debounceTime(this.debounceTime),
					map((e) => e && e.target),
				)
				.subscribe((e: HTMLInputElement) => {

					if (!e.value) {
						this.resetSearchResults()
					} else if (e && e.value.length >= this.minCharLength) {
						const query = e.value as string;
						this.fetchResults(query)
					}
				});
		}
	}

	/**
	 * Clean up the results state
	 * @returns void
	 */
	private resetSearchResults = () => {
		this.state.set((prevState) => ({ ...prevState, results: defaultState.results }));
		this.domUtils.toggleElementState(this.goToResultsPageLink, ['hide'])
		if (this.searchInputDebounced.value) this.searchInputDebounced.value = ''
	}

	/**
	 * Fetch the results or return cached results if exists
	 * @returns Promise<void>
	 */
	private async fetchResults(searchText: string): Promise<void> {
		const cache = this.state.get().cache
		if (cache && cache[searchText]) {
			this.state.set((prevState) => ({ ...prevState, results: cache[searchText] }));
		} else {
			this.queryTypeaheadResults(searchText)
				.then((response) => response.json())
				.then((searchResults: MoleculeSearchAutosuggestionHitsProps) => {
					this.searchResultsPage = searchResults.summaryMessage.url || null;
					this.state.set((prevState) => ({
						...prevState,
						results: searchResults,
						cache: {
							...prevState.cache,
							[searchText]: searchResults
						}
					}));
				})
				.catch((e) => {
					// console.log(e)
					this.resetSearchResults()
				});
		}
	}

	/**
	 * Inform the autosuggestion hits component with new results
	 * @returns void
	 */
	private onUpdateResults = (currentState: InputSearchDebounceState) => {
		this.dispatchCustomEvent(SearchSuggestionResultsEvents.UPDATE_SEARCH_SUGGESTION_RESULTS, currentState.results, false, true)
	}

	private registerEvents() {
		window.addEventListener(InputSearchDebounceEvents.RESET_SEARCH, this.resetSearchResults)
	}

}
